import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { API_ENDPOINTS } from '../constants/api-endpoints';

export interface PatronUser {
  patron_id: number;
  userid: string;
  firstname?: string;
  surname?: string;
  email?: string;
  category_id?: string;
  library_id?: string;
  dateofbirth?: string;
  phone?: string;
}

@Injectable({ providedIn: 'root' })
export class PatronAuthService {
  private http = inject(HttpClient);
  
  // Store the patron's own Basic Auth token (for patron-specific endpoints)
  private patronToken: string | null = null;
  private currentUserSubject = new BehaviorSubject<PatronUser | null>(null);
  
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
    this.restoreSession();
  }

  /**
   * Check if a patron is currently logged in.
   */
  get isLoggedIn(): boolean {
    return !!this.patronToken;
  }

  /**
   * Get the current patron's own Authorization header.
   * Used exclusively for patron-specific endpoints (e.g. change password).
   */
  getPatronAuthorizationHeader(): string | null {
    return this.patronToken ? `Basic ${this.patronToken}` : null;
  }

  /**
   * Authenticate a patron using Koha's credential-validation endpoint.
   *
   * HOW IT WORKS:
   * ─────────────────────────────────────────────────────────────────
   * POST /api/v1/auth/password/validation requires `borrowers:1` permission —
   * this means the REQUEST must be authenticated as a STAFF user.
   *
   * ┌──────────────┐   POST /auth/password/validation              ┌──────────────┐
   * │   Angular    │  ─── Authorization: Basic [STAFF creds] ────► │  Koha API    │
   * │     App      │  ─── Body: {identifier, password}  ─────────► │  (validates) │
   * │              │  ◄── 201: {patron_id, userid} ─────────────── │              │
   * └──────────────┘                                                └──────────────┘
   *
   * The STAFF credentials flow through the interceptor automatically.
   * We do NOT add the patron's credentials to this request.
   *
   * After validation we:
   *  1. Store the patron's own Basic Auth token for patron-specific calls
   *  2. Fetch the full patron profile (also via staff auth)
   *
   * @param userid  - patron's userid or cardnumber
   * @param password - patron's plain-text password
   */
  login(userid: string, password: string): Observable<PatronUser> {
    // Step 1: Validate credentials — NO custom Authorization header here.
    // The interceptor attaches STAFF credentials which satisfy borrowers:1 permission.
    return this.http.post<{ patron_id: number; userid: string; cardnumber: string }>(
      `${API_ENDPOINTS.BASE}/auth/password/validation`,
      { identifier: userid, password }
      // Headers deliberately omitted — let the staff interceptor handle auth
    ).pipe(
      switchMap(result => {
        // Step 2: Credentials are valid. Store the patron's own token for
        // patron-specific endpoints (e.g. change password).
        const patronToken = btoa(`${userid}:${password}`);
        this.patronToken = patronToken;
        localStorage.setItem('patron_token', patronToken);

        // Step 3: Fetch the full patron profile using staff auth.
        return this.http.get<PatronUser>(`${API_ENDPOINTS.BASE}/patrons/${result.patron_id}`);
      }),
      tap((patron: PatronUser) => {
        this.currentUserSubject.next(patron);
        localStorage.setItem('patron_user', JSON.stringify(patron));
      }),
      catchError(err => {
        console.error('[PatronAuthService] Login failed', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Change a patron's password via the public Koha endpoint.
   * POST /api/v1/public/patrons/{id}/password
   *
   * This endpoint is called WITH the patron's own credentials (not staff),
   * because it is a public endpoint that requires the patron to prove they
   * know their current password.
   *
   * @param patronId           - the patron's Koha ID
   * @param oldPassword        - current plain-text password (required by Koha)
   * @param newPassword        - new plain-text password
   * @param newPasswordRepeated - repeated new password for server-side validation
   */
  changePassword(patronId: number, oldPassword: string, newPassword: string, newPasswordRepeated: string): Observable<any> {
    if (!this.patronToken) {
      return throwError(() => new Error('Patron not logged in'));
    }
    // This endpoint uses the PATRON'S own credentials — override the staff header
    const headers = new HttpHeaders({
      'Authorization': `Basic ${this.patronToken}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    });

    return this.http.post(
      `${API_ENDPOINTS.BASE}/public/patrons/${patronId}/password`,
      { old_password: oldPassword, password: newPassword, password_repeated: newPasswordRepeated },
      { headers }
    ).pipe(
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Log out the patron — clears in-memory state and localStorage.
   */
  logout(): void {
    this.patronToken = null;
    this.currentUserSubject.next(null);
    localStorage.removeItem('patron_token');
    localStorage.removeItem('patron_user');
  }

  /**
   * Restore session from localStorage on app startup.
   */
  private restoreSession(): void {
    const token = localStorage.getItem('patron_token');
    const userStr = localStorage.getItem('patron_user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.patronToken = token;
        this.currentUserSubject.next(user);
      } catch (e) {
        this.logout();
      }
    }
  }
}

